import * as React from 'react';
import { createRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { CacResponse, LtvResponse, ProjectCashResponse, TcoResponse } from '@bits/shared/zod';
import { formatInr, paise } from '@bits/domain';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { SortableTh } from '@/components/ui/sortable-th';
import { useSortedRows, type SortAccessors } from '@/lib/sort';

export const analyticsEconomicsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/analytics/economics',
  component: EconomicsPage,
});

function monthShift(n: number): string {
  const now = new Date();
  const ist = new Date(now.getTime() + (5 * 60 + 30) * 60 * 1000);
  const idx = ist.getUTCFullYear() * 12 + ist.getUTCMonth() + n;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`;
}

function inr(p: number): string {
  return formatInr(paise(p));
}

const money = 'whitespace-nowrap py-2 pr-3 text-right font-mono';
const th = 'whitespace-nowrap py-2 pr-3 font-medium';

type CacRow = CacResponse['rows'][number];
type CacSortKey = 'channel' | 'spend' | 'newClients' | 'cac';
const EMPTY_CAC: CacRow[] = [];
const cacSortAccessors: SortAccessors<CacRow, CacSortKey> = {
  channel: (r) => r.channelName,
  spend: (r) => r.spendPaise,
  newClients: (r) => r.newClients,
  cac: (r) => r.cacPaise,
};

type LtvRow = LtvResponse['rows'][number];
type LtvSortKey = 'client' | 'channel' | 'revenue' | 'cost' | 'ltv' | 'cacShare' | 'payback';
const EMPTY_LTV: LtvRow[] = [];
const ltvSortAccessors: SortAccessors<LtvRow, LtvSortKey> = {
  client: (r) => r.clientName,
  channel: (r) => r.channelName,
  revenue: (r) => r.revenuePaise,
  cost: (r) => r.costPaise,
  ltv: (r) => r.marginPaise,
  cacShare: (r) => r.cacSharePaise,
  payback: (r) => r.paybackMonths,
};

type CashRow = ProjectCashResponse['rows'][number];
type CashSortKey = 'project' | 'cashIn' | 'cashOut' | 'npv' | 'irr';
const EMPTY_CASH: CashRow[] = [];
const cashSortAccessors: SortAccessors<CashRow, CashSortKey> = {
  project: (r) => `${r.code} · ${r.name}`,
  cashIn: (r) => r.inPaise,
  cashOut: (r) => r.outPaise,
  npv: (r) => r.npvPaise,
  irr: (r) => r.irrBpp,
};

type TcoRow = TcoResponse['rows'][number];
type TcoSortKey = 'project' | 'payroll' | 'expenses' | 'overhead' | 'tco';
const EMPTY_TCO: TcoRow[] = [];
const tcoSortAccessors: SortAccessors<TcoRow, TcoSortKey> = {
  project: (r) => `${r.code} · ${r.name}`,
  payroll: (r) => r.payrollCostPaise,
  expenses: (r) => r.expenseCostPaise,
  overhead: (r) => r.overheadSharePaise,
  tco: (r) => r.tcoPaise,
};

function EconomicsPage(): JSX.Element {
  const [from, setFrom] = React.useState(monthShift(-11));
  const [to, setTo] = React.useState(monthShift(0));
  const [rateBpp, setRateBpp] = React.useState(1200);

  const cacQuery = useQuery({ queryKey: ['analytics', 'cac', from, to], queryFn: () => api.getCac(from, to) });
  const ltvQuery = useQuery({ queryKey: ['analytics', 'ltv'], queryFn: () => api.getLtv(36) });
  const cashQuery = useQuery({ queryKey: ['analytics', 'cash', rateBpp], queryFn: () => api.getProjectCash(rateBpp) });
  const tcoQuery = useQuery({ queryKey: ['analytics', 'tco'], queryFn: () => api.getTco() });

  const { rows: cacRows, ...cacSorter } = useSortedRows(cacQuery.data?.rows ?? EMPTY_CAC, cacSortAccessors);
  const { rows: ltvRows, ...ltvSorter } = useSortedRows(ltvQuery.data?.rows ?? EMPTY_LTV, ltvSortAccessors);
  const { rows: cashRows, ...cashSorter } = useSortedRows(cashQuery.data?.rows ?? EMPTY_CASH, cashSortAccessors);
  const { rows: tcoRows, ...tcoSorter } = useSortedRows(tcoQuery.data?.rows ?? EMPTY_TCO, tcoSortAccessors);

  return (
    <AppShell>
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <Link
            to="/analytics"
            className="text-xs font-medium text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
          >
            ← Analytics
          </Link>
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
            Lead economics & cash
          </h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            CAC and LTV per channel/client, project payback, NPV/IRR on mapped cash, and TCO. The
            basis is labelled on every card.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-end justify-between gap-4">
          <div>
            <CardTitle className="text-base">CAC per channel</CardTitle>
            <CardDescription>
              <Badge variant="muted">Cash</Badge> channel spend ÷ new clients with that first-touch
              channel, in the window.
            </CardDescription>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex flex-col gap-1.5">
              <Label>From</Label>
              <Input type="month" value={from} onChange={(e) => setFrom(e.currentTarget.value)} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>To</Label>
              <Input type="month" value={to} onChange={(e) => setTo(e.currentTarget.value)} />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {cacRows.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              No channel spend or new clients in this window. Record spend under CRM → Lead Channels.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-[hsl(var(--muted-foreground))]">
                  <tr>
                    <SortableTh sortKey="channel" controller={cacSorter} className={th}>
                      Channel
                    </SortableTh>
                    <SortableTh sortKey="spend" controller={cacSorter} align="right" className={th}>
                      Spend
                    </SortableTh>
                    <SortableTh sortKey="newClients" controller={cacSorter} align="right" className={th}>
                      New clients
                    </SortableTh>
                    <SortableTh sortKey="cac" controller={cacSorter} align="right" className={th}>
                      CAC
                    </SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {cacRows.map((r) => (
                    <tr key={r.channelId} className="border-t border-[hsl(var(--border))]">
                      <td className="min-w-[10rem] py-2 pr-3">{r.channelName}</td>
                      <td className={money}>{inr(r.spendPaise)}</td>
                      <td className={money}>{r.newClients}</td>
                      <td className={money}>{r.cacPaise === null ? '—' : inr(r.cacPaise)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">LTV & payback per client</CardTitle>
          <CardDescription>
            <Badge variant="muted">Accrual</Badge> Σ gross margin per client over the trailing 36
            months; payback is months until cumulative margin covers the client's CAC share
            (channel CAC in its acquisition month).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {ltvRows.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No client margin yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-[hsl(var(--muted-foreground))]">
                  <tr>
                    <SortableTh sortKey="client" controller={ltvSorter} className={th}>
                      Client
                    </SortableTh>
                    <SortableTh sortKey="channel" controller={ltvSorter} className={th}>
                      Channel
                    </SortableTh>
                    <SortableTh sortKey="revenue" controller={ltvSorter} align="right" className={th}>
                      Revenue
                    </SortableTh>
                    <SortableTh sortKey="cost" controller={ltvSorter} align="right" className={th}>
                      Cost
                    </SortableTh>
                    <SortableTh sortKey="ltv" controller={ltvSorter} align="right" className={th}>
                      LTV (margin)
                    </SortableTh>
                    <SortableTh sortKey="cacShare" controller={ltvSorter} align="right" className={th}>
                      CAC share
                    </SortableTh>
                    <SortableTh sortKey="payback" controller={ltvSorter} align="right" className={th}>
                      Payback
                    </SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {ltvRows.map((r) => (
                    <tr key={r.clientId} className="border-t border-[hsl(var(--border))]">
                      <td className="min-w-[10rem] py-2 pr-3">
                        <Link to="/crm/clients/$clientId" params={{ clientId: r.clientId }} className="hover:underline">
                          {r.clientName}
                        </Link>
                      </td>
                      <td className="min-w-[10rem] py-2 pr-3">{r.channelName}</td>
                      <td className={money}>{inr(r.revenuePaise)}</td>
                      <td className={money}>{inr(r.costPaise)}</td>
                      <td className={`${money} font-semibold`}>{inr(r.marginPaise)}</td>
                      <td className={money}>{r.cacSharePaise === null ? '—' : inr(r.cacSharePaise)}</td>
                      <td className={money}>
                        {r.paybackMonths === null ? 'not yet' : r.paybackMonths === 0 ? 'immediate' : `${r.paybackMonths} mo`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-end justify-between gap-4">
          <div>
            <CardTitle className="text-base">Project NPV / IRR</CardTitle>
            <CardDescription>
              <Badge variant="muted">Cash</Badge> mapped in/out transactions per project, discounted
              monthly at the annual rate.
            </CardDescription>
          </div>
          <div className="flex w-40 flex-col gap-1.5">
            <Label>Annual rate</Label>
            <Select value={String(rateBpp)} onChange={(e) => setRateBpp(Number(e.currentTarget.value))}>
              <option value="800">8%</option>
              <option value="1000">10%</option>
              <option value="1200">12%</option>
              <option value="1500">15%</option>
              <option value="1800">18%</option>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {cashRows.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No project-mapped cash yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-[hsl(var(--muted-foreground))]">
                  <tr>
                    <SortableTh sortKey="project" controller={cashSorter} className={th}>
                      Project
                    </SortableTh>
                    <SortableTh sortKey="cashIn" controller={cashSorter} align="right" className={th}>
                      Cash in
                    </SortableTh>
                    <SortableTh sortKey="cashOut" controller={cashSorter} align="right" className={th}>
                      Cash out
                    </SortableTh>
                    <SortableTh sortKey="npv" controller={cashSorter} align="right" className={th}>
                      NPV
                    </SortableTh>
                    <SortableTh sortKey="irr" controller={cashSorter} align="right" className={th}>
                      IRR
                    </SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {cashRows.map((r) => (
                    <tr key={r.projectId} className="border-t border-[hsl(var(--border))]">
                      <td className="min-w-[10rem] py-2 pr-3">
                        <Link to="/projects/$projectId" params={{ projectId: r.projectId }} className="hover:underline">
                          {r.code} · {r.name}
                        </Link>
                      </td>
                      <td className={money}>{inr(r.inPaise)}</td>
                      <td className={money}>{inr(r.outPaise)}</td>
                      <td className={`${money} ${r.npvPaise < 0 ? 'text-red-700' : ''}`}>{inr(r.npvPaise)}</td>
                      <td className={money}>{r.irrBpp === null ? '—' : `${(r.irrBpp / 100).toFixed(1)}%`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">TCO per project</CardTitle>
          <CardDescription>
            <Badge variant="muted">Accrual</Badge> posted payroll + mapped expenses + a pro-rata share
            of overhead (cost-center spend{tcoQuery.data ? `, ${inr(tcoQuery.data.overheadPaise)} total` : ''}).
          </CardDescription>
        </CardHeader>
        <CardContent>
          {tcoRows.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No project cost yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-[hsl(var(--muted-foreground))]">
                  <tr>
                    <SortableTh sortKey="project" controller={tcoSorter} className={th}>
                      Project
                    </SortableTh>
                    <SortableTh sortKey="payroll" controller={tcoSorter} align="right" className={th}>
                      Payroll
                    </SortableTh>
                    <SortableTh sortKey="expenses" controller={tcoSorter} align="right" className={th}>
                      Expenses
                    </SortableTh>
                    <SortableTh sortKey="overhead" controller={tcoSorter} align="right" className={th}>
                      Overhead share
                    </SortableTh>
                    <SortableTh sortKey="tco" controller={tcoSorter} align="right" className={th}>
                      TCO
                    </SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {tcoRows.map((r) => (
                    <tr key={r.projectId} className="border-t border-[hsl(var(--border))]">
                      <td className="min-w-[10rem] py-2 pr-3">{r.code} · {r.name}</td>
                      <td className={money}>{inr(r.payrollCostPaise)}</td>
                      <td className={money}>{inr(r.expenseCostPaise)}</td>
                      <td className={money}>{inr(r.overheadSharePaise)}</td>
                      <td className={`${money} font-semibold`}>{inr(r.tcoPaise)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

    </AppShell>
  );
}
